import isEqual from 'date-fns/esm/fp/isEqual/index.js';
import React from 'react';
import { map, cloneDeep, filter, isEmpty, forEach, isArray } from 'lodash';

import { formSection as FormSectionView } from 'ui/views/widgets/formSection';

// React view component to render

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { FormSection } from 'ui/containers/FormSection';
 *
 * // render
 * <FormSection propName='propValue'/>
 */
export class FormSection extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods
        this.handleFormSubmit = this.handleFormSubmit.bind( this );
        this.handleAddButtonClick = this.handleAddButtonClick.bind( this );
        this.handleRemoveAllButtonClick = this.handleRemoveAllButtonClick.bind( this );
        this.handleRemoveRow = this.handleRemoveRow.bind( this );
        this.handleCancelClick = this.handleCancelClick.bind( this );
        this.handleEditRow = this.handleEditRow.bind( this );
        this.handleEditFormCancelClick = this.handleEditFormCancelClick.bind( this );

        this.dataBeforeEdit = [];

        this.subFieldsRecords =  isArray( props.value ) && !isEmpty( props.value ) ? [ { subFields: cloneDeep( props.config.subFields ) } ] : []; // if values present add subfield data on initialization
        this.subFieldsRecordsBeforeEdit = [];

        this.state = {
            isFormVisible: false,
            values: isArray( props.value ) && !isEmpty( props.value ) ? props.value : [], // if values present use those on initialization
        };
    }

    handleFormSubmit( values, subFields ) {
        log.debug( 'FormSection.handleFormSubmit()' );

        const formIsEmpty = Object.values( values ).every( x => null === x || '' === x );

        if( formIsEmpty ) { // if all fields are empty dont add entry in form
            return null;
        }

        this.subFieldsRecords = [ ...this.subFieldsRecords, { subFields } ];
        
        this.setState( {
            isFormVisible: false,
            values: [ ...this.state.values, values ]
        }, () => this.props.onChange( {
            name: this.props.name,
            value: this.state.values,
        } ) );
    }


    handleRemoveAllButtonClick( ) {
        log.debug( 'FormSection.handleRemoveAllButtonClick()' );

        this.subFieldsRecords = [];

        this.setState( {
            values: []
        }, () => this.props.onChange( {
            name: this.props.name,
            value: [],
        } ) );
    }

    handleAddButtonClick() {
        log.debug( 'FormSection.handleAddButtonClick()' );
        
        this.setState( {
            isFormVisible: true,
            editableValues: {},
            subFields: cloneDeep( this.props.config.subFields )
        } );
    }

    handleCancelClick() {
        log.debug( 'FormSection.handleCancelClick()' );

        this.setState( {
            isFormVisible: false
        } );
    }

    handleEditFormCancelClick() {
        log.debug( 'FormSection.handleEditFormCancelClick()' );

        this.subFieldsRecords = this.subFieldsRecordsBeforeEdit;

        this.setState( {
            values: this.dataBeforeEdit,
            isFormVisible: false
        } );
    }

    handleRemoveRow( index ) {
        log.debug( 'FormSection.handleRemoveRow()' );

        // handle subfields record removal
        const subFieldsRecords = map( cloneDeep( this.subFieldsRecords ), ( record, _index ) => {
            if( ! isEqual( _index, index ) ) {
                return record;
            }

            return null;
        } );

        this.subFieldsRecords = filter( subFieldsRecords, record => ! isEmpty( record ) );

        // handle value object removal
        const _values = cloneDeep( this.state.values );

        const values = map( _values, ( _value, _index ) => {
            if( !isEqual( _index, index ) ) {
                return _value;
            }
            return null;
        } );

        this.setState( {
            values: filter( values, val => !isEmpty( val ) )
        }, () => this.props.onChange( {
            name: this.props.name,
            value: filter( values, val => !isEmpty( val ) )
        } ) );
    }

    handleEditRow( index ) {
        log.debug( 'FormSection.handleEditRow()' );

        // return immediately if form is already open
        if ( this.state.isFormVisible ) {
            return;
        }

        // handle modification of sub fields records
        let _recordAtIndex = [];
        const _subFieldsRecords = cloneDeep( this.subFieldsRecords );
        this.subFieldsRecordsBeforeEdit = cloneDeep( this.subFieldsRecords );

        forEach( _subFieldsRecords, ( record, _index ) => {
            if( isEqual( _index, index ) ) {
                _recordAtIndex =  record;
            }
        } );

        const subFieldsRecords = map( _subFieldsRecords, ( record, _index ) => {
            if ( ! isEqual( _index, index ) ) {
                return record;
            }

            return null;
        } );

        this.subFieldsRecords = filter( subFieldsRecords, record => ! isEmpty( record ) );


        // handle modificatin of values
        const _values = cloneDeep( this.state.values );
        this.dataBeforeEdit = cloneDeep( this.state.values );
        let _valueAtIndex = {};

        forEach( _values, ( _value, _index ) => {
            if( isEqual( _index, index ) ) {
                _valueAtIndex =  _value;
            }
        } );

        const values = map( _values, ( _value, _index ) => {
            if( !isEqual( _index, index ) ) {
                return _value;
            }
            return null;
        } );


        this.setState( {
            isFormVisible: true,
            editableValues: _valueAtIndex,
            values: filter( values, val => !isEmpty( val ) ),
            subFields: _recordAtIndex.subFields
        } );
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <FormSectionView
                { ...this.props }
                { ...this.props.config }
                { ...this.state }
                onFormSectionSubmit = { this.handleFormSubmit }
                onAddButtonClick = { this.handleAddButtonClick }
                onRemoveAllButtonClick = { this.handleRemoveAllButtonClick }
                onEditRow = { this.handleEditRow }
                onRemoveRow = { this.handleRemoveRow }
                onCancelClick = { this.handleCancelClick }
                onEditFormCancelClick= { this.handleEditFormCancelClick }
            />
        );
    }
}

// set display name
FormSection.displayName = 'FormSection';

// set default props
FormSection.defaultProps = {};

// prop types
FormSection.propTypes = {};
